import { useCallback, useEffect, useState } from 'react';
import { ancestorIdsForPath, type DocTreeRoot } from '../lib/docTree';

export const DOC_TREE_EXPANDED_KEY = 'alma-doc-tree-expanded';
export const DOC_TREE_PINNED_KEY = 'alma-doc-tree-pinned';

function readExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(DOC_TREE_EXPANDED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((id): id is string => typeof id === 'string'));
    }
  } catch {
    // Storage unavailable or corrupted — start collapsed.
  }
  return new Set();
}

function persistExpanded(ids: Set<string>): void {
  try {
    localStorage.setItem(DOC_TREE_EXPANDED_KEY, JSON.stringify([...ids]));
  } catch {
    // Storage unavailable — the in-memory state still works this session.
  }
}

/**
 * Expansion state for the documents file tree: which folder ids are open,
 * persisted across sessions. Navigating to a document ADDS its ancestor
 * folders (auto-reveal) — it never collapses anything the user opened.
 */
export function useDocTreeState(roots: DocTreeRoot[], activePath?: string | null) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(readExpanded);

  useEffect(() => {
    if (!activePath || roots.length === 0) return;
    const ancestors = ancestorIdsForPath(roots, activePath);
    if (ancestors.length === 0) return;
    setExpandedIds((prev) => {
      if (ancestors.every((id) => prev.has(id))) return prev;
      const next = new Set(prev);
      for (const id of ancestors) next.add(id);
      persistExpanded(next);
      return next;
    });
  }, [activePath, roots]);

  const toggleFolder = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      persistExpanded(next);
      return next;
    });
  }, []);

  return { expandedIds, toggleFolder };
}

/**
 * Whether the desktop docked tree pane is pinned open (default) or collapsed,
 * persisted across sessions.
 */
export function useDocTreePinned() {
  const [pinned, setPinnedState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DOC_TREE_PINNED_KEY) !== '0';
    } catch {
      return true;
    }
  });

  const setPinned = useCallback((value: boolean) => {
    setPinnedState(value);
    try {
      localStorage.setItem(DOC_TREE_PINNED_KEY, value ? '1' : '0');
    } catch {
      // Storage unavailable — the in-memory state still works this session.
    }
  }, []);

  return { pinned, setPinned };
}
